import { Except, SetOptional } from '@common/types/common.type';
import { autoUpdate, limitShift, offset, shift, useFloating } from '@floating-ui/react';
import { SubjectId } from '@master/constants/subject.constant';
import { clsxm } from '@ui/utils/style.util';
import { getValueFromEnum } from '@util/utils/object.util';
import pick from 'lodash/pick';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import {
  STANDARD_DETAIL_DARK_STYLE,
  STANDARD_DETAIL_NORMAL_STYLE,
  StandardDetailColorNumber,
} from '../../constants/standard.constant';
import { StandardQuestionDetailItemUnderlineSchema } from '../../models/standard-question-detail.model';
import {
  getTextOrientationStyleFromSubjectId,
  splitQuestionParagraphsWithNumber,
  splitQuestionSentencesWithNumber,
  StandardNumberedText,
} from '../../utils/standard.util';

// #region Types
type StandardQuestionBodyContentProps = ComponentProps<'p'> & {
  subjectId?: SubjectId | null;
};
export type StandardNumberedQuestionBodyContentHighlight = SetOptional<
  StandardQuestionDetailItemUnderlineSchema,
  'paragraphNumber' | 'sentenceNumber'
> & {
  colorNumber?: StandardDetailColorNumber;
  displayNumber?: number;
  labels?: string[];
  underline?: boolean;
};
type StandardNumberedQuestionBodyContentProps = Except<
  StandardQuestionBodyContentProps,
  'children'
> & {
  emptyInfo?: string;
  highlights?: StandardNumberedQuestionBodyContentHighlight[];
  numberedQuestionParagraphs?: StandardNumberedText[];
  questionText?: string | null;
  withEmptyInfo?: boolean;
};
type StandardNumberedQuestionBodyContentParagraphItemHighlight =
  StandardNumberedQuestionBodyContentHighlight & {
    shouldDisplayNumber?: boolean;
  };
type StandardNumberedQuestionBodyContentParagraphItem = {
  highlights?: StandardNumberedQuestionBodyContentParagraphItemHighlight[];
  startIndex: number;
  text: string;
};

type StandardDetailQuestionBodyContentParagraphProps = {
  paragraph: StandardNumberedText;
  paragraphItems: StandardNumberedQuestionBodyContentParagraphItem[];
};
enum StandardDetailQuestionBodyContentParagraphItemLabelType {
  First = 'first',
  Last = 'last',
}
type StandardDetailQuestionBodyContentParagraphItemProps = Except<
  StandardNumberedQuestionBodyContentParagraphItem,
  'startIndex'
> &
  ComponentProps<'span'> & {
    containerRef: HTMLSpanElement | null;
    previousHighlights?: StandardNumberedQuestionBodyContentParagraphItemHighlight[];
  };
// #endregion

// #region Constants
export const STANDARD_DETAIL_QUESTION_BODY_CONTENT_PARAGRAPH_NUMBER_ATTRIBUTE =
  'data-paragraphnumber';
export const STANDARD_DETAIL_QUESTION_BODY_CONTENT_SENTENCE_OFFSET_ATTRIBUTE =
  'data-sentenceoffset';
// #endregion

// #region Utils
function processParagraphNumberedTextsHighlighting(
  paragraphs?: StandardNumberedText[],
  highlights?: StandardNumberedQuestionBodyContentHighlight[],
) {
  let innerHighlights = [...(highlights ?? [])];

  return paragraphs?.map((paragraph) => {
    let items: StandardNumberedQuestionBodyContentParagraphItem[] = [
      { startIndex: 0, text: paragraph.text },
    ];
    const result = { paragraph, paragraphItems: items };

    const sentences = splitQuestionSentencesWithNumber(paragraph.text) ?? [];
    const newInnerHighlights: StandardNumberedQuestionBodyContentHighlight[] = [];
    const targetHighlights = innerHighlights
      ?.filter((highlightItem) => {
        let isIncluded =
          paragraph.text.includes(highlightItem.underlinedText) && !!highlightItem.underlinedText;
        if (highlightItem.paragraphNumber != null) {
          isIncluded &&= paragraph.number === highlightItem.paragraphNumber;

          if (highlightItem.sentenceNumber != null && isIncluded) {
            isIncluded &&= sentences.some(
              (sentence) =>
                sentence.number === highlightItem.sentenceNumber &&
                sentence.text.includes(highlightItem.underlinedText),
            );
          }
        }

        if (!isIncluded) newInnerHighlights.push(highlightItem);

        return isIncluded;
      })
      .map((highlight) =>
        pick(highlight, [
          'underlinedText',
          'sentenceNumber',
          'paragraphNumber',
          'labels',
          'underline',
          'colorNumber',
          'displayNumber',
        ]),
      );
    innerHighlights = newInnerHighlights;

    if (targetHighlights.length <= 0) return result;

    let minimumCharacterIndex = paragraph.text.length;
    let maximumCharacterIndex = 0;

    const detailedHiglights = targetHighlights.map((highlight) => {
      let startIndex = paragraph.text.indexOf(highlight.underlinedText);
      if (highlight.sentenceNumber != null) {
        let tempStartIndex = 0;
        sentences.slice(0, highlight.sentenceNumber).forEach((sentence) => {
          if (sentence.number === highlight.sentenceNumber) {
            tempStartIndex += sentence.text.indexOf(highlight.underlinedText);
          } else tempStartIndex += sentence.text.length;
        });
        startIndex = tempStartIndex;
      }
      const endIndex = startIndex + highlight.underlinedText.length;

      if (startIndex < minimumCharacterIndex) minimumCharacterIndex = startIndex;
      if (endIndex > maximumCharacterIndex) maximumCharacterIndex = endIndex;

      return { ...highlight, endIndex, startIndex };
    });

    if (minimumCharacterIndex >= maximumCharacterIndex) return result;

    items = [{ startIndex: 0, text: paragraph.text.slice(0, minimumCharacterIndex) }];

    const firstHighlightIndexes: number[] = [];
    let currentStartIndex = minimumCharacterIndex;
    paragraph.text
      .slice(minimumCharacterIndex, maximumCharacterIndex)
      .split('')
      .forEach((character, characterIndex) => {
        const characterHighlights: StandardNumberedQuestionBodyContentParagraphItemHighlight[] = [];
        detailedHiglights.forEach((highlight, higlightIndex) => {
          const startIndex = highlight.startIndex - minimumCharacterIndex;
          const endIndex = highlight.endIndex - minimumCharacterIndex;

          if (characterIndex < startIndex || characterIndex >= endIndex) return;

          const shouldDisplayNumber = !firstHighlightIndexes.includes(higlightIndex);
          if (shouldDisplayNumber) firstHighlightIndexes.push(higlightIndex);

          characterHighlights.push({ ...highlight, shouldDisplayNumber });
        });
        if (characterHighlights.length <= 0) {
          if (!items?.[items.length - 1]?.highlights?.length) {
            items[items.length - 1].text += character;
          } else items.push({ startIndex: currentStartIndex, text: character });

          currentStartIndex += character.length;
          return;
        }

        const previousSubstring = items?.[items.length - 1];
        const previousHighlights = previousSubstring?.highlights;
        let shouldConcatenate = !characterHighlights.some(
          (highlight) => highlight.shouldDisplayNumber,
        );
        shouldConcatenate &&= characterHighlights.length === previousHighlights?.length;
        shouldConcatenate &&= characterHighlights.every((highlight, highlightIndex) =>
          Object.entries(highlight).every(([key, value]) => {
            const previousHighlight = previousHighlights?.[highlightIndex];
            const castedKey =
              key as keyof StandardNumberedQuestionBodyContentParagraphItemHighlight;
            if (castedKey === 'shouldDisplayNumber') return true;
            if (castedKey === 'labels' && Array.isArray(value)) {
              const targetPreviousLabels = previousHighlight?.labels;

              return value.every(
                (label, labelIndex) => targetPreviousLabels?.[labelIndex] === label,
              );
            }
            if (Number.isNaN(value) && Number.isNaN(previousHighlight?.[castedKey])) return true;

            return value === previousHighlight?.[castedKey];
          }),
        );

        if (shouldConcatenate) items[items.length - 1].text += character;
        else {
          items.push({
            highlights: characterHighlights,
            startIndex: currentStartIndex,
            text: character,
          });
        }
        currentStartIndex += character.length;
      });

    items.push({
      startIndex: currentStartIndex,
      text: paragraph.text.slice(maximumCharacterIndex),
    });
    items = items.filter((substring) => substring.text.length > 0);
    result.paragraphItems = items;

    return result;
  });
}
// #endregion

// #region Sub Components
function StandardDetailQuestionBodyContentParagraphItem({
  className,
  containerRef,
  highlights,
  previousHighlights,
  text,
  ...rest
}: StandardDetailQuestionBodyContentParagraphItemProps) {
  // #region Computed Values
  const firstLabel = useMemo(
    () =>
      highlights
        ?.map((highlight) => highlight?.labels?.[0])
        .filter((item) => !!item?.trim())
        .join(', '),
    [highlights],
  );
  const lastLabel = useMemo(
    () =>
      highlights
        ?.map((highlight) => highlight?.labels?.[1])
        .filter((item) => !!item?.trim())
        .join(', '),
    [highlights],
  );
  const colorNumber = useMemo(() => highlights?.at(-1)?.colorNumber, [highlights]);
  const underline = useMemo(
    () => !!highlights?.some((highlight) => highlight.underline),
    [highlights],
  );
  const displayText = useMemo(() => {
    let newText = text;
    if (firstLabel != null) newText = newText.slice(1);
    if (lastLabel != null) newText = newText.slice(0, -1);

    return newText;
  }, [firstLabel, lastLabel, text]);
  const displayNumber = useMemo(
    () =>
      highlights
        ?.map((highlight, highlightIndex) => {
          const previousHighlight = previousHighlights?.[highlightIndex];
          if (!previousHighlight) return highlight;

          return {
            ...highlight,
            shouldDisplayNumber:
              previousHighlight.shouldDisplayNumber &&
              highlight.shouldDisplayNumber &&
              previousHighlight.displayNumber === highlight.displayNumber
                ? false
                : highlight.shouldDisplayNumber,
          };
        })
        .filter((highlight) => highlight.shouldDisplayNumber)
        .map((highlight) => highlight.displayNumber)
        .join(', '),
    [highlights, previousHighlights],
  );
  // #endregion

  // #region Floatings
  const firstLabelFloating = useFloating({
    middleware: [
      offset({ mainAxis: -4 }),
      shift({ boundary: containerRef ?? undefined, limiter: limitShift() }),
    ],
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
  });
  const lastLabelFloating = useFloating({
    middleware: [
      offset({ mainAxis: -4 }),
      shift({ boundary: containerRef ?? undefined, limiter: limitShift() }),
    ],
    placement: 'right-end',
    whileElementsMounted: autoUpdate,
  });
  // #endregion

  // #region Handlers
  const renderLabel = useCallback(
    (label: string, type: StandardDetailQuestionBodyContentParagraphItemLabelType) => (
      <span
        ref={getValueFromEnum(type, {
          [StandardDetailQuestionBodyContentParagraphItemLabelType.First]:
            firstLabelFloating.refs.setReference,
          [StandardDetailQuestionBodyContentParagraphItemLabelType.Last]:
            lastLabelFloating.refs.setReference,
        })}
        className="relative"
      >
        {getValueFromEnum(type, {
          [StandardDetailQuestionBodyContentParagraphItemLabelType.First]: text.at(0),
          [StandardDetailQuestionBodyContentParagraphItemLabelType.Last]: text.at(-1),
        })}
        <span
          key={`question-body-content-label-${text}-${label}-${type}`}
          ref={getValueFromEnum(type, {
            [StandardDetailQuestionBodyContentParagraphItemLabelType.First]:
              firstLabelFloating.refs.setFloating,
            [StandardDetailQuestionBodyContentParagraphItemLabelType.Last]:
              lastLabelFloating.refs.setFloating,
          })}
          className={clsxm(
            'py-0.5 text-xs text-white-snow whitespace-nowrap select-none',
            colorNumber != null && STANDARD_DETAIL_DARK_STYLE[colorNumber],
          )}
          style={getValueFromEnum(type, {
            [StandardDetailQuestionBodyContentParagraphItemLabelType.First]: {
              ...firstLabelFloating.floatingStyles,
            },
            [StandardDetailQuestionBodyContentParagraphItemLabelType.Last]: {
              ...lastLabelFloating.floatingStyles,
            },
          })}
        >
          {label}
        </span>
      </span>
    ),
    [
      firstLabelFloating.refs.setReference,
      firstLabelFloating.refs.setFloating,
      firstLabelFloating.floatingStyles,
      lastLabelFloating.refs.setReference,
      lastLabelFloating.refs.setFloating,
      lastLabelFloating.floatingStyles,
      text,
      colorNumber,
    ],
  );
  // #endregion

  return (
    <span
      {...rest}
      className={clsxm(
        'relative',
        colorNumber != null && STANDARD_DETAIL_NORMAL_STYLE[colorNumber],
        underline && 'underline',
        className,
      )}
      style={{ textUnderlinePosition: underline ? 'right' : undefined }}
    >
      {firstLabel != null &&
        renderLabel(firstLabel, StandardDetailQuestionBodyContentParagraphItemLabelType.First)}
      {displayText}
      {displayNumber && (
        <span
          key={`question-body-content-display-number-${text}-${displayNumber}`}
          className="absolute z-[1] -top-1.5 -right-2 max-w-7 text-center text-[10px] border border-grey-input-border rounded-full px-1 bg-white-snow/80"
          style={{ writingMode: 'initial' }}
        >
          {displayNumber}
        </span>
      )}
      {lastLabel != null &&
        renderLabel(lastLabel, StandardDetailQuestionBodyContentParagraphItemLabelType.Last)}
    </span>
  );
}
function StandardDetailQuestionBodyContentParagraph({
  paragraph,
  paragraphItems,
}: StandardDetailQuestionBodyContentParagraphProps) {
  // #region Refs
  const [containerRef, setContainerRef] = useState<HTMLSpanElement | null>(null);
  // #endregion

  // #region Contents
  const renderedItems = useMemo(() => {
    const paragraphAttributes: Record<string, unknown> = {
      [STANDARD_DETAIL_QUESTION_BODY_CONTENT_PARAGRAPH_NUMBER_ATTRIBUTE]: paragraph.number,
    };

    return paragraphItems.map(({ startIndex, ...item }, substringIndex) => {
      if (substringIndex > 0) {
        paragraphAttributes[STANDARD_DETAIL_QUESTION_BODY_CONTENT_SENTENCE_OFFSET_ATTRIBUTE] =
          startIndex;
      }

      return (
        <StandardDetailQuestionBodyContentParagraphItem
          key={`question-body-content-paragraph-item-${item.text}-${substringIndex.toString()}`}
          containerRef={containerRef}
          previousHighlights={
            substringIndex > 0 ? paragraphItems[substringIndex - 1]?.highlights : undefined
          }
          {...item}
          {...paragraphAttributes}
        />
      );
    });
  }, [paragraph.number, paragraphItems, containerRef]);
  // #endregion

  return (
    <>
      <span className="font-bold select-none w-fit h-fit" style={{ writingMode: 'initial' }}>
        {paragraph.number}
      </span>
      <span ref={setContainerRef} className="w-full h-full">
        {renderedItems}
      </span>
    </>
  );
}
// #endregion

function BaseStandardDetailQuestionBodyContent({
  children,
  className,
  subjectId,
  ...rest
}: StandardQuestionBodyContentProps) {
  return (
    <p
      style={getTextOrientationStyleFromSubjectId(subjectId)}
      {...rest}
      className={clsxm(
        'px-3 py-5 border-l-[0.5px] border-grey-input-border whitespace-pre-wrap',
        getValueFromEnum(subjectId, {
          [SubjectId.English]: 'overflow-y-auto',
          [SubjectId.Japanese]: 'overflow-x-auto',
        }),
        className,
      )}
    >
      {children}
    </p>
  );
}

function StandardDetailQuestionBodyContentNumbered({
  className,
  emptyInfo,
  highlights,
  numberedQuestionParagraphs,
  questionText,
  subjectId,
  withEmptyInfo,
  ...rest
}: StandardNumberedQuestionBodyContentProps) {
  // #region Content
  const content = useMemo(() => {
    const render = (paragraphs?: StandardNumberedText[]) => {
      return processParagraphNumberedTextsHighlighting(paragraphs, highlights)?.map(
        ({ paragraph, paragraphItems }, index) => (
          <StandardDetailQuestionBodyContentParagraph
            key={`question-body-content-paragraph-${paragraph.text}-${
              paragraph.number
            }-${index.toString()}`}
            paragraph={paragraph}
            paragraphItems={paragraphItems}
          />
        ),
      );
    };

    if (numberedQuestionParagraphs?.length) return render(numberedQuestionParagraphs);

    return render(splitQuestionParagraphsWithNumber(questionText));
  }, [numberedQuestionParagraphs, questionText, highlights]);
  // #endregion

  return (
    <BaseStandardDetailQuestionBodyContent
      {...rest}
      className={clsxm(
        subjectId === SubjectId.Japanese && 'border-0',
        content?.length
          ? 'grid grid-cols-[max-content_minmax(0,1fr)] place-items-baseline gap-x-1.5'
          : withEmptyInfo && 'text-red-deep',
        className,
      )}
      subjectId={subjectId}
    >
      {content?.length ? content : withEmptyInfo && emptyInfo}
    </BaseStandardDetailQuestionBodyContent>
  );
}

const StandardDetailQuestionBodyContent = Object.assign(BaseStandardDetailQuestionBodyContent, {
  Numbered: StandardDetailQuestionBodyContentNumbered,
});

export default StandardDetailQuestionBodyContent;
